import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  AccessForSelectFragment,
  UpdatePerformanceProcessTemplateAuthorDocument,
} from 'generated/resources/graphql'

import { AccessSelector } from 'modules/core/components/AccessSelector'
import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type Props = {
  onClose: () => void
  templateId: number
  author?: AccessForSelectFragment | null
}

export const ReplaceAuthorModal: React.FC<Props> = ({ templateId, author, onClose }) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [selectedAccess, setSelectedAccess] = React.useState<AccessForSelectFragment | null>(
    author || null
  )
  const [updateAuthor, { loading }] = useMutation(UpdatePerformanceProcessTemplateAuthorDocument, {
    extractorFn: (data) =>
      data.performanceMutations.updateAuthorPerformanceReviewProcessCustomTemplate,
  })

  const handleSelectAccess = (newAccess: AccessForSelectFragment | null) => {
    setSelectedAccess(newAccess)
  }

  const handleReplaceOwner = async () => {
    if (!selectedAccess) {
      return
    }

    const response = await updateAuthor({
      variables: {
        id: templateId,
        authorId: selectedAccess?.id,
      },
    })

    const result = handleResult(response, (errors) => {
      app.setFlash({
        type: 'wrong',
        message: errors.messages || i18n.t('errors.generic_error'),
      })
    })

    if (result.success) {
      const canManage = result.data.reviewProcessCustomTemplate?._policies?.manage.can ?? false

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      if (!canManage) {
        navigate(navigation.performanceProcess.reviews.templates.fullPath())
      }
      onClose()
    }
  }

  return (
    <Modal
      illustration="user-male-female"
      title={i18n.t('performance_process.reviews.templates.owners.replace_modal.title')}
      description={
        <Box gap="s16">
          <Text.Normal size="300" overflowWrap color="textSecondary">
            {i18n.t('performance_process.reviews.templates.owners.replace_modal.description')}
          </Text.Normal>
          <Callout
            type="warning"
            description={i18n.t('performance_process.reviews.templates.owners.replace_modal.note')}
          />
        </Box>
      }
      onClose={onClose}
      onSubmit={handleReplaceOwner}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={loading || !selectedAccess} loading={loading}>
            {i18n.t('performance_process.reviews.templates.owners.replace_modal.replace')}
          </Button>
        </>
      }
    >
      <AccessSelector
        width="s400"
        label={i18n.t('performance_process.reviews.templates.owners.replace_modal.selector_label')}
        emptyLabel={i18n.t(
          'performance_process.reviews.templates.owners.replace_modal.selector_empty_state'
        )}
        terminated={false}
        mandatory
        value={selectedAccess}
        withAvatar
        onSelect={handleSelectAccess}
        withRegularAccess
      />
    </Modal>
  )
}
